import React, { useState, useEffect } from "react";
import "react-dropdown/style.css";
import "./editRobotSettings.scss";
import Modal from "../modal/modal";
import Dropdown from "react-dropdown";
import ApiHandler from "../api";

const apiHandler = new ApiHandler();

function EditRobotSettingsComponent({ robot, closeModal, onRobotUpdated, user }) {
  const [robotData, setRobotData] = useState(() => ({
    ...robot,
    settings: { ...(robot?.settings || {}) },
  }));

  const [isLoading, setIsLoading] = useState(false);

  // Technicians can only edit specific fields
  const isTechnician = user?.role === "technician";
  const technicianAllowedSettings = [
    "driveMotorType",
    "cleaningMotorType",
    "batterySize",
  ];
  const canEditSetting = (settingKey) => {
    if (!isTechnician) return true;
    return technicianAllowedSettings.includes(settingKey);
  };

  useEffect(() => {
    if (robot && robot._id !== robotData._id) {
      setRobotData((prevState) => ({
        ...robot,
        settings: { ...(robot?.settings || {}) },
      }));
    }
  }, [robot, robotData._id]);

  const onChange = (value, param) => {
    const { settings } = robotData;
    settings[param] = isNaN(Number(value)) ? value : Number(value);
    setRobotData({ ...robotData, settings });
  };

  const onSubmit = async () => {
    let hasChanges = false;
    Object.keys(robotData.settings).forEach((key) => {
      if (robotData.settings[key] !== robot.settings[key]) {
        hasChanges = true;
      }
    });
    if (!hasChanges) {
      closeModal(false);
      return;
    }

    try {
      setIsLoading(true);

      const params = { settings: robotData.settings };

      await apiHandler.setRobotSettings(robot._id, params);

      setIsLoading(false);

      if (onRobotUpdated) {
        onRobotUpdated(robotData);
      }
      closeModal(true);
    } catch (err) {
      setIsLoading(false);
      alert(err.message);
    }
  };

  const getTitle = () => {
    return `Robot ${robot.robotName} Settings`;
  };

  // Motor options
  const motorOptions = {
    driveMotorOptions: [
      { value: "4.5_NM_1_23", label: "4.5 Nm (1:23)" },
      { value: "6.5_NM_1_47", label: "6.5 Nm (1:47)" },
      {
        value: "4_NM_MOB16V_3060RPM_1_51",
        label: "4 Nm MOB16V 3060RPM (1:51)",
      },
      { value: "6_NM_PN_MOB160665_1_59", label: "6 Nm PN. MOB160665 (1:59)" },
    ],
    cleaningMotorOptions: [
      { value: "4.5_NM_1_15", label: "4.5 Nm (1:15)" },
      { value: "6.5_NM_200RPM_1_18", label: "6.5 Nm 200rpm (1:18)" },
      { value: "6.5_NM_250RPM_1_23", label: "6.5 Nm 250rpm (1:23)" },
      {
        value: "5_NM_MOB16V_4000RPM_1_15",
        label: "5 Nm MOB16V 4000RPM (1:15)",
      },
      {
        value: "5_NM_PN_MOB1605250",
        label: "5 Nm PN.MOB1605250 (1:14)",
      },
    ],
  };

  const operationModeOptions = [
    { value: "autonomous", label: "Autonomous" },
    { value: "portable", label: "Portable" },
    { value: "inactive", label: "Inactive" },
    { value: "inactiveClient", label: "Inactive Client" },
    { value: "inactiveAirtouch", label: "Inactive Airtouch" },
  ];

  const batterySizeOptions = [
    { value: "9", label: "9" },
    { value: "12", label: "12" },
    { value: "18", label: "18" },
    { value: "24", label: "24" },
    { value: "30", label: "30" },
  ];

  const items = [
    {
      text: "Drive Motor Type",
      value: "driveMotorType",
      type: "dropdown",
      options: motorOptions.driveMotorOptions,
    },
    {
      text: "Cleaning Motor Type",
      value: "cleaningMotorType",
      type: "dropdown",
      options: motorOptions.cleaningMotorOptions,
    },
    {
      text: "Drive Speed Transit [m/min]",
      value: "driveSpeedTransit",
      type: "number",
      min: 12,
      max: 20,
    },
    {
      text: "Drive Speed Cleaning [m/min]",
      value: "driveSpeedCleaning",
      type: "number",
      min: 12,
      max: 20,
    },
    {
      text: "Rotor Speed Transit [RPM]",
      value: "rotorSpeedTransit",
      type: "number",
      min: 5,
      max: 200,
    },
    {
      text: "Rotor Speed Cleaning [RPM]",
      value: "rotorSpeedCleaning",
      type: "number",
      min: 5,
      max: 200,
    },
    {
      text: "Stop Distance Transit [mm]",
      value: "stopDistanceTransit",
      type: "number",
      min: 100,
      max: 500,
    },
    {
      text: "Stop Distance Cleaning [mm]",
      value: "stopDistanceCleaning",
      type: "number",
      min: 100,
      max: 500,
    },
    {
      text: "0.5 Sec Current Threshold Drive Motor [Amp]",
      value: "halfSecCurrentThresholdDriveMotor",
      type: "number",
      min: 4,
      max: 27,
    },
    {
      text: "0.5 Sec Current Threshold Cleaning Motor [Amp]",
      value: "halfSecCurrentThresholdCleaningMotor",
      type: "number",
      min: 15,
      max: 22,
    },
    {
      text: "2 Sec Current Threshold Drive Motor [Amp]",
      value: "twoSecCurrentThresholdDriveMotor",
      type: "number",
      min: 4,
      max: 9,
    },
    {
      text: "2 Sec Current Threshold Cleaning Motor [Amp]",
      value: "twoSecCurrentThresholdCleaningMotor",
      type: "number",
      min: 8,
      max: 20,
    },
    {
      text: "RPM Target Ratio Drive Motor [%]",
      value: "rpmTargetRatioDriveMotor",
      type: "number",
      min: 40,
      max: 90,
    },
    {
      text: "RPM Target Ratio Cleaning Motor [%]",
      value: "rpmTargetRatioCleaningMotor",
      type: "number",
      min: 50,
      max: 95,
    },
    {
      text: "Clean Motor Speed Ratio While Testing [%]",
      value: "cleanMotorSpeedRatioWhileTestingPERC",
      type: "number",
      min: 70,
      max: 95,
    },
    {
      text: "Battery Threshold High Load [V]",
      value: "batteryThresholdHighLoad",
      type: "number",
      min: 14.5,
      max: 15.7,
      step: 0.1,
    },
    {
      text: "Battery Threshold Low Load [V]",
      value: "batteryThresholdLowLoad",
      type: "number",
      min: 14.5,
      max: 15.7,
      step: 0.1,
    },
    {
      text: "Reserve Battery Threshold [Ah]",
      value: "reserveBatteryThresholdAh",
      type: "number",
      min: 1,
      max: 4,
    },
    {
      text: "Battery Voltage Threshold [V]",
      value: "batteryVoltageThreshold",
      type: "number",
      min: 14.5,
      max: 16.4,
      step: 0.1,
    },
    {
      text: "Battery Size [Ah]",
      value: "batterySize",
      type: "dropdown",
      options: batterySizeOptions,
    },
    {
      text: "Row Edges Double Cleaning Distance [m]",
      value: "rowEdgesDoubleCleaningDistance",
      type: "number",
      min: 0,
      max: 100,
    },
    {
      text: "Clean Motor Min Expected Current [Amp]",
      value: "cleanMotorMinExpectedCurrentAMP",
      type: "number",
      min: 0.1,
      max: 4,
      step: 0.1,
    },
    {
      text: "Fast Drying",
      value: "fastDrying",
      type: "checkbox",
    },
    {
      text: "Charge Fix During Battery Test",
      value: "chargeFixDuringBatteryTest",
      type: "checkbox",
    },
    {
      text: "Jammed Robot Fix On Table",
      value: "jammedRobotFixOnTable",
      type: "checkbox",
    },
    {
      text: "Operation Mode",
      value: "operationMode",
      type: "dropdown",
      options: operationModeOptions,
    },
  ];

  return (
    <Modal
      title={getTitle()}
      onClose={() => closeModal(false)}
      className="edit-robot-settings"
    >
      <div className="settings-grid">
        {items.map((item) =>
          item.type === "checkbox" ? (
            <div key={item.value} className="input-line checkbox">
              <input
                checked={robotData.settings[item.value] || false}
                type={item.type}
                disabled={!canEditSetting(item.value)}
                onChange={(e) => onChange(e.target.checked, item.value)}
              />
              <div className="input-description">{item.text}</div>
            </div>
          ) : item.type === "dropdown" ? (
            <div key={item.value} className="input-line">
              <div className="input-description">{item.text}</div>
              <Dropdown
                options={item.options}
                disabled={!canEditSetting(item.value)}
                onChange={(option) => onChange(option.value, item.value)}
                value={
                  robotData.settings[item.value] !== undefined
                    ? robotData.settings[item.value] + ""
                    : ""
                }
                className="dropdown"
                controlClassName="control"
                placeholderClassName="placeholder"
              />
            </div>
          ) : (
            <div key={item.value} className="input-line">
              <div className="input-description">{item.text}</div>
              <input
                value={robotData.settings[item.value] || ""}
                type={item.type}
                min={item.min}
                max={item.max}
                step={item.step}
                disabled={!canEditSetting(item.value)}
                onChange={(e) => onChange(e.target.value, item.value)}
              />
            </div>
          )
        )}
      </div>

      <div className="buttons-wrapper">
        <div className="button cancel" onClick={() => closeModal(false)}>
          CANCEL
        </div>
        <div
          className={`button ${isLoading ? "loading" : ""}`}
          onClick={onSubmit}
          disabled={isLoading}
        >
          {isLoading ? "SAVING..." : "SUBMIT"}
        </div>
      </div>
    </Modal>
  );
}

export default EditRobotSettingsComponent;
